import React, { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PhoneOff, Mic, MicOff, Loader2, RotateCw, MessageSquare, Sparkles } from "lucide-react";
import { VoiceWaveform } from "@/components/VoiceWaveform";
import { Portal } from "@/components/Portal";
import { RealtimeVoice, EMPLOYEE_VOICE, employeeInstructions, NORA_TOOLS, detectAssociate, detectHangup, isEmailCapable, getVoiceFor } from "@/lib/realtime";
import { gmail, twilio as twilioApi, contacts as contactsApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";

// The star of the demo: a live, voice-first call with an AI associate,
// powered by OpenAI Realtime (WebRTC). Speak naturally — they hear and reply.
export function VoiceCall({ employee, onClose, greet = true, onSwitchToText, associates = [], onRoute, initialQuery = null }) {
  const { user } = useAuth();
  const userName = user?.call_name || "Lewis";
  const [status, setStatus] = useState("connecting"); // connecting | live | error | idle
  const [speaking, setSpeaking] = useState(false);
  const [listening, setListening] = useState(false);
  const [muted, setMuted] = useState(false);
  const [transcript, setTranscript] = useState([]);
  const voiceRef = useRef(null);
  const scroller = useRef(null);
  const empListRef = useRef(associates);
  const routeRef = useRef(onRoute);
  const curIdRef = useRef(employee.id);
  const endRef = useRef(() => {});
  useEffect(() => { empListRef.current = associates; }, [associates]);
  useEffect(() => { routeRef.current = onRoute; }, [onRoute]);
  useEffect(() => { curIdRef.current = employee.id; }, [employee.id]);

  const onState = useCallback((s) => {
    if (s.status) setStatus(s.status);
    if (typeof s.speaking === "boolean") setSpeaking(s.speaking);
    if (typeof s.listening === "boolean") setListening(s.listening);
    if (s.aiTranscript) setTranscript((t) => [...t, { from: "ai", text: s.aiTranscript }]);
    if (s.userTranscript) {
      setTranscript((t) => [...t, { from: "user", text: s.userTranscript }]);
      // Leave the call by voice — say "bye", "that's all", "back to HQ", etc.
      if (detectHangup(s.userTranscript)) { endRef.current(); return; }
      // Say another associate's name mid-call to hop straight over to them.
      const id = detectAssociate(s.userTranscript, empListRef.current, { requireIntent: true });
      if (id && id !== curIdRef.current && routeRef.current) routeRef.current(id);
    }
  }, []);

  const connect = useCallback(async () => {
    setStatus("connecting");
    setTranscript([]);
    try {
      const rv = new RealtimeVoice(onState);
      voiceRef.current = rv;
      // Pull this associate's training knowledge + company knowledge to inject.
      let knowledgeText = "";
      try {
        const kc = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/knowledge/context?associate_id=${employee.id}`, { credentials: "include" }).then((r) => r.json());
        if (kc && kc.has_knowledge) knowledgeText = kc.text;
      } catch (_) {}
      let instr = employeeInstructions(employee, userName, knowledgeText);
      // If the user already asked something when naming this associate (e.g.
      // "Hi Miles, how's your day?"), greet briefly then answer that question.
      const effectiveGreet = initialQuery
        ? `${userName} just said to you: "${initialQuery}". Begin your reply now: open with a brief "Hi ${userName}," then directly answer what was asked, staying fully in character as ${employee.name}. Keep it short and natural — do not restate your name or title beyond the greeting.`
        : greet;
      const opts = { greet: effectiveGreet, transcribe: true, userName };
      if (isEmailCapable(employee.id)) {
        try { const c = await gmail.context(); if (c.connected && c.context) instr += "\n\n" + c.context; } catch (_) {}
        try {
          const d = await gmail.directory();
          if (d.people && d.people.length) {
            const list = d.people.map((p) => `${p.name || p.email} <${p.email}>`).join(", ");
            instr += `\n\nAuthorized team members you can email with the send_email_to_user tool: ${list}. When the user asks you to email one of them, draft it, read it back, and only send after they confirm.`;
          }
        } catch (_) {}
        try {
          const cc = await contactsApi.list();
          if (cc.items && cc.items.length) {
            const list = cc.items.map((c) => `${c.name}${c.phone ? " (" + c.phone + ")" : ""}${c.notes ? " — " + c.notes : ""}`).join("; ");
            instr += `\n\nSaved contacts you can call or text by name (the system looks up their number automatically): ${list}. When the user asks you to call or text one of them by name, use their name as recipient_name / contact_name — you don't need to ask for the number.`;
          }
        } catch (_) {}
        try {
          const nowLocal = new Date();
          instr += `\n\nThe current date and time is ${nowLocal.toString()}. Use this to compute the send_at timestamp when the user asks you to schedule a text for later (e.g. "tomorrow at 9am").`;
        } catch (_) {}
        opts.tools = NORA_TOOLS;
        opts.onToolCall = async (name, args) => {
          if (name === "send_support_reply") {
            return await gmail.voiceReply(args.email_subject, args.reply_body);
          }
          if (name === "send_email_to_user") {
            return await gmail.sendToUser(args.recipient, args.subject, args.body);
          }
          if (name === "place_outbound_call") {
            return await twilioApi.outbound(args.phone_number, args.recipient_name, args.purpose);
          }
          if (name === "send_sms") {
            return await twilioApi.sendSms(args.phone_number, args.contact_name, args.message);
          }
          if (name === "schedule_sms") {
            return await twilioApi.scheduleSms(args.phone_number, args.contact_name, args.message, args.send_at);
          }
          return { ok: false, error: "unknown tool" };
        };
      }
      await rv.start(instr, getVoiceFor(employee.id), opts);
    } catch (_) {
      setStatus("error");
      voiceRef.current && voiceRef.current.stop();
      voiceRef.current = null;
    }
  }, [employee, greet, initialQuery, onState, userName]);

  useEffect(() => {
    connect();
    return () => { voiceRef.current && voiceRef.current.stop(); voiceRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employee.id]);

  useEffect(() => { scroller.current?.scrollTo({ top: scroller.current.scrollHeight, behavior: "smooth" }); }, [transcript]);

  const end = () => { voiceRef.current && voiceRef.current.stop(); voiceRef.current = null; onClose(); };
  useEffect(() => { endRef.current = end; });
  const toggleMute = () => { const m = !muted; setMuted(m); voiceRef.current && voiceRef.current.setMuted(m); };

  const statusLabel = status === "connecting" ? "Connecting…" : status === "error" ? "Couldn't connect" : speaking ? `${employee.name} is speaking…` : listening ? "Listening to you…" : "Live · say hello";
  const active = speaking || listening;
  const color = speaking ? "#8A2BE2" : "#00E5FF";

  return (
    <Portal>
    <AnimatePresence>
      <div className="fixed inset-0 z-[90] grid place-items-center p-4" role="dialog" aria-modal="true" data-testid="voice-call">
        <motion.div className="absolute inset-0 bg-[#030A16]/85 backdrop-blur-md" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={end} />
        <motion.div
          className="glass-strong relative flex w-full max-w-md flex-col items-center overflow-hidden rounded-3xl p-8"
          initial={{ scale: 0.92, y: 24, opacity: 0 }} animate={{ scale: 1, y: 0, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}
          transition={{ type: "spring", stiffness: 220, damping: 24 }}
        >
          <div className="pointer-events-none absolute -top-24 left-1/2 h-64 w-64 -translate-x-1/2 rounded-full" style={{ background: `radial-gradient(circle, ${color}33, transparent 70%)`, transition: "background 0.4s" }} />

          <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-[#00E5FF]"><Sparkles size={12} /> Live voice</div>

          {/* pulsing avatar */}
          <div className="relative mt-5 grid place-items-center">
            {active && [0, 1].map((i) => (
              <motion.span key={i} className="absolute rounded-full" style={{ width: 132, height: 132, border: `1px solid ${color}` }}
                initial={{ opacity: 0.5, scale: 1 }} animate={{ opacity: 0, scale: 1.5 }} transition={{ duration: 1.6, repeat: Infinity, delay: i * 0.6 }} />
            ))}
            <motion.img
              src={employee.avatar} alt={employee.name}
              className="relative h-28 w-28 rounded-3xl object-cover"
              style={{ boxShadow: `0 0 30px ${color}88`, border: `2px solid ${color}` }}
              animate={speaking ? { scale: [1, 1.04, 1] } : { scale: 1 }} transition={{ duration: 0.7, repeat: speaking ? Infinity : 0 }}
            />
          </div>

          <div className="mt-4 text-center">
            <div className="font-display text-2xl font-semibold text-white">{employee.name}</div>
            <div className="text-sm text-slate-400">{employee.title}</div>
          </div>

          <div className="mt-3 flex items-center gap-2 rounded-full border border-white/10 px-3 py-1 text-xs text-slate-200" data-testid="voice-call-status">
            {status === "connecting" && <Loader2 size={13} className="animate-spin text-[#00E5FF]" />}
            {status !== "connecting" && status !== "error" && <span className="h-2 w-2 rounded-full" style={{ background: color, boxShadow: `0 0 8px ${color}` }} />}
            {statusLabel}
          </div>

          <VoiceWaveform active={active} height={44} bars={32} color={color} className="mt-5 w-full" />

          {status === "error" ? (
            <div className="mt-5 w-full rounded-xl border border-[#FF0055]/25 bg-[#FF0055]/5 p-4 text-center text-sm text-slate-200" data-testid="voice-call-error">
              I couldn't start the microphone. Please allow mic access in your browser and try again.
              <div className="mt-3 flex justify-center gap-2">
                <button onClick={connect} className="flex items-center gap-1.5 rounded-lg bg-[#00E5FF] px-3 py-1.5 text-xs font-semibold text-[#030A16]" data-testid="voice-call-retry"><RotateCw size={13} /> Try again</button>
                {onSwitchToText && <button onClick={() => { end(); onSwitchToText(); }} className="flex items-center gap-1.5 rounded-lg border border-white/10 px-3 py-1.5 text-xs text-slate-200"><MessageSquare size={13} /> Use text chat</button>}
              </div>
            </div>
          ) : (
            <>
              {/* live transcript */}
              <div ref={scroller} className="mt-5 h-28 w-full space-y-1.5 overflow-y-auto rounded-xl border border-white/8 bg-[#030A16]/40 p-3" data-testid="voice-call-transcript">
                {transcript.length === 0 && <div className="text-center text-xs text-slate-500">{status === "connecting" ? "Setting up your call…" : "Say “Hello” to begin — I'm listening."}</div>}
                {transcript.slice(-8).map((m, i) => (
                  <div key={i} className={`text-sm ${m.from === "user" ? "text-slate-300" : "text-white"}`}>
                    <span className="mr-1.5 font-mono text-[10px] uppercase text-slate-500">{m.from === "user" ? "You" : employee.name}</span>{m.text}
                  </div>
                ))}
              </div>
            </>
          )}
        </motion.div>
      </div>
    </AnimatePresence>
    </Portal>
  );
}
